// Vacaciones (Arts. 69-71 CT): saldos por empleado (15 días/año + adicionales
// por antigüedad desde el 6.º año) y registro de días gozados.
// Nómina → Beneficios Sociales.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, History, Loader2, Trash2, DollarSign, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { Campo, Modal, Switch, fecha, fieldCls, nomRpc, useDialogoEnLinea, type VacacionGoce, type VacacionProvision, type VacacionSaldo } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';

export function Vacaciones() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [soloActivos, setSoloActivos] = useState(true);
  const [q, setQ] = useState('');
  const { data: saldos = [], isFetching } = useQuery<VacacionSaldo[]>({
    queryKey: ['nom-vacaciones', soloActivos],
    queryFn: () => nomRpc<VacacionSaldo[]>('nom_vacaciones_saldos', { _solo_activos: soloActivos }),
  });

  const filas = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return saldos;
    return saldos.filter((s) => s.razon_social.toLowerCase().includes(t) || s.identificacion.includes(t));
  }, [saldos, q]);

  const [registrando, setRegistrando] = useState<VacacionSaldo | null>(null);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [obs, setObs] = useState('');
  const dias = desde && hasta && hasta >= desde
    ? Math.round((new Date(hasta).getTime() - new Date(desde).getTime()) / 86400000) + 1
    : 0;

  const [historial, setHistorial] = useState<VacacionSaldo | null>(null);
  const { data: goces = [], isFetching: cargandoGoces } = useQuery<VacacionGoce[]>({
    queryKey: ['nom-vac-goces', historial?.empleado_id],
    queryFn: () => nomRpc<VacacionGoce[]>('nom_vacaciones_goces', { _empleado_id: historial!.empleado_id }),
    enabled: !!historial,
  });
  const { data: provision } = useQuery<VacacionProvision>({
    queryKey: ['nom-vac-provision', historial?.empleado_id],
    queryFn: () => nomRpc<VacacionProvision>('nom_vacaciones_provision', { _empleado_id: historial!.empleado_id }),
    enabled: !!historial,
  });

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['nom-vacaciones'] });
    void qc.invalidateQueries({ queryKey: ['nom-vac-goces'] });
    void qc.invalidateQueries({ queryKey: ['nom-vac-provision'] });
  };
  const liquidar = useMutation({
    mutationFn: (goceId: string) => nomRpc<{ valor: number; provision_aplicada: number; gasto_adicional: number; asiento_numero: number }>(
      'nom_vacacion_liquidar', { _data: { goce_id: goceId } }),
    onSuccess: (r) => {
      toast.success(`Liquidado $${Number(r.valor).toFixed(2)} — asiento N.º ${r.asiento_numero}` +
        (Number(r.gasto_adicional) > 0 ? ` (provisión $${Number(r.provision_aplicada).toFixed(2)} + gasto $${Number(r.gasto_adicional).toFixed(2)})` : ' (con cargo a la provisión)'));
      invalidar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const anularLiq = useMutation({
    mutationFn: (id: string) => nomRpc('nom_vacacion_liquidacion_anular', { _id: id }),
    onSuccess: () => { toast.success('Liquidación anulada (asiento revertido)'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const registrar = useMutation({
    mutationFn: () => nomRpc('nom_vacacion_registrar', {
      _data: { empleado_id: registrando!.empleado_id, desde, hasta, observacion: obs || null },
    }),
    onSuccess: () => { toast.success('Vacación registrada'); setRegistrando(null); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => nomRpc('nom_vacacion_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Registro eliminado'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const headersExp = ['N°', 'Código', 'Empleado', 'Ingreso', 'Años', 'Ganados', 'Gozados', 'Saldo'];
  const filasExport: Celda[][] = [
    ...filas.map((s, i): Celda[] => [
      i + 1, s.empleado_codigo, s.razon_social, fecha(s.fecha_ingreso),
      Number(s.anios_completos), Number(s.dias_ganados), Number(s.dias_gozados), Number(s.saldo),
    ]),
    ['', '', '', '', 'TOTAL',
      filas.reduce((a, s) => a + Number(s.dias_ganados), 0),
      filas.reduce((a, s) => a + Number(s.dias_gozados), 0),
      filas.reduce((a, s) => a + Number(s.saldo), 0)],
  ];

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Vacaciones</h1>
        <ExportarBotones nombre={`vacaciones-${fechaLocal()}`} titulo="Vacaciones"
          headers={headersExp} filas={filasExport} colsDerecha={[0, 4, 5, 6, 7]} disabled={filas.length === 0} />
      </div>
      <p className="text-xs text-muted-foreground">
        Art. 69 CT: 15 días anuales ininterrumpidos (incluye no laborables); desde el 6.º año, un día adicional por
        año (máx. 15 adicionales; la elección de gozarlos o pagarlos es del empleador, Art. 70). Acumulables hasta 3 años (Art. 75).
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <input className={`${fieldCls} w-72`} placeholder="Filtrar por nombre o cédula…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Switch checked={soloActivos} onChange={setSoloActivos} label="Solo activos" />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Saldos ({filas.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Empleado</th><th className="py-2 pr-3">Ingreso</th>
              <th className="py-2 pr-3 text-right">Años</th><th className="py-2 pr-3 text-right">Ganados</th>
              <th className="py-2 pr-3 text-right">Gozados</th><th className="py-2 pr-3 text-right">Saldo</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filas.map((s) => (
                <tr key={s.empleado_id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{s.empleado_codigo}</td>
                  <td className="py-2 pr-3">{s.razon_social}<div className="text-xs text-muted-foreground font-mono">{s.identificacion}</div></td>
                  <td className="py-2 pr-3 font-mono">{fecha(s.fecha_ingreso)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{s.anios_completos}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{Number(s.dias_ganados).toFixed(1)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{Number(s.dias_gozados).toFixed(1)}</td>
                  <td className={`py-2 pr-3 text-right font-mono tabular-nums font-semibold ${Number(s.saldo) < 5 ? 'text-pendiente' : ''}`}>{Number(s.saldo).toFixed(1)}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Registrar goce"
                      onClick={() => { setRegistrando(s); setDesde(''); setHasta(''); setObs(''); }}><CalendarPlus className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted ml-1" title="Historial"
                      onClick={() => setHistorial(s)}><History className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {registrando && (
        <Modal title={`Registrar vacaciones — ${registrando.razon_social}`} onClose={() => setRegistrando(null)}>
          <div className="space-y-3">
            <div className="text-sm">Saldo disponible: <span className="font-mono font-semibold">{Number(registrando.saldo).toFixed(1)} días</span></div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Desde *"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
              <Campo label="Hasta *"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
            </div>
            {dias > 0 && <div className="text-sm">{dias} días calendario (incluye no laborables, Art. 69)</div>}
            <Campo label="Observación"><input className={fieldCls} value={obs} onChange={(e) => setObs(e.target.value)} /></Campo>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setRegistrando(null)}>Cancelar</Button>
              <Button isLoading={registrar.isPending} disabled={dias <= 0} onClick={() => registrar.mutate()}>Registrar {dias > 0 ? `${dias} días` : ''}</Button>
            </div>
          </div>
        </Modal>
      )}

      {historial && (
        <Modal title={`Historial de vacaciones — ${historial.razon_social}`} onClose={() => setHistorial(null)} wide>
          <div className="space-y-3">
            {provision && (
              <div className="flex flex-wrap gap-4 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                <span>Provisión acumulada: <b className="font-mono">${Number(provision.acumulada).toFixed(2)}</b></span>
                <span>Liquidada: <b className="font-mono">${Number(provision.liquidada).toFixed(2)}</b></span>
                <span>Disponible: <b className={`font-mono ${Number(provision.disponible) > 0 ? 'text-primary' : ''}`}>${Number(provision.disponible).toFixed(2)}</b></span>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Liquidar valora el goce (salario diario × días) y descarga la provisión (2.1.03.03); el faltante va a gasto
              (5.2.09). Genera un asiento con crédito a Sueldos por pagar (2.1.03.01).
            </p>
            {cargandoGoces && <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></div>}
            {!cargandoGoces && goces.length === 0 && <div className="py-4 text-center text-muted-foreground text-sm">Sin vacaciones registradas</div>}
            {goces.length > 0 && (
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">Desde</th><th className="py-2 pr-3">Hasta</th><th className="py-2 pr-3 text-right">Días</th>
                  <th className="py-2 pr-3">Observación</th><th className="py-2 pr-3">Liquidación</th><th className="py-2"></th>
                </tr></thead>
                <tbody>
                  {goces.map((g) => (
                    <tr key={g.id} className="border-b last:border-0">
                      <td className="py-2 pr-3 font-mono">{fecha(g.desde)}</td>
                      <td className="py-2 pr-3 font-mono">{fecha(g.hasta)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{Number(g.dias)}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{g.observacion}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {g.liquidacion_id ? (
                          <span className="inline-flex items-center gap-1 text-xs">
                            <span className="font-mono font-semibold">${Number(g.liq_valor).toFixed(2)}</span>
                            <span className="text-muted-foreground">· asiento N.º {g.asiento_numero}</span>
                            <button className="inline-flex w-6 h-6 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Anular liquidación"
                              onClick={async () => { if (await dlg.confirmar('¿Anular la liquidación?', { detalle: 'Se revierte el asiento y se libera el goce.', peligro: true, aceptar: 'Anular' })) anularLiq.mutate(g.liquidacion_id!); }}><Undo2 className="w-3.5 h-3.5" /></button>
                          </span>
                        ) : (
                          <Button size="sm" variant="outline" isLoading={liquidar.isPending && liquidar.variables === g.id}
                            onClick={async () => { if (await dlg.confirmar(`¿Liquidar ${Number(g.dias)} días de vacaciones?`, { detalle: 'Se generará el asiento contable.', aceptar: 'Liquidar' })) liquidar.mutate(g.id); }}>
                            <DollarSign className="w-3.5 h-3.5 mr-1" /> Liquidar
                          </Button>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        {!g.liquidacion_id && (
                          <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10" title="Eliminar"
                            onClick={async () => { if (await dlg.confirmar('¿Eliminar el registro?', { peligro: true, aceptar: 'Eliminar' })) eliminar.mutate(g.id); }}><Trash2 className="w-4 h-4" /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
